import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./common/Icon";

interface PortalShellProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  headerLeft?: React.ReactNode;
  headerRight?: React.ReactNode;
  /** Custom back-panel content. If provided, ··· button flips to this panel. */
  settingsPanel?: React.ReactNode;
}

export function PortalShell({
  title,
  onClose,
  children,
  headerLeft,
  headerRight,
  settingsPanel,
}: PortalShellProps) {
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (flipped) { setFlipped(false); return; }
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose, flipped]);

  const hasSettings = !!settingsPanel;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_150ms_ease-out]" />

      <div
        className="relative w-full h-[90vh] max-w-[90vw] lg:max-w-[85vw] xl:max-w-6xl"
        style={{ perspective: hasSettings ? "1200px" : undefined }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative w-full h-full"
          style={hasSettings ? {
            transformStyle: "preserve-3d",
            transition: "transform 500ms ease-in-out",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          } : undefined}
        >
          {/* ─── Front ─── */}
          <div
            className="bg-white dark:bg-[#171719] rounded shadow-2xl flex flex-col animate-[scaleIn_200ms_ease-out]"
            style={hasSettings
              ? { position: "absolute", inset: 0, backfaceVisibility: "hidden" }
              : { width: "100%", height: "100%" }
            }
          >
            <div className="flex items-center px-5 h-14 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                {headerLeft ?? (
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</h2>
                )}
              </div>
              <div className="flex items-center gap-1">
                {headerRight ?? (
                  hasSettings ? (
                    <button
                      onClick={() => setFlipped(true)}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    >
                      <Icon name="more_horiz" size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                  ) : (
                    <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                      <Icon name="more_horiz" size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                  )
                )}
                <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                  <Icon name="close" size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>
            {children}
          </div>

          {/* ─── Back ─── */}
          {hasSettings && (
            <div
              className="absolute inset-0 bg-white dark:bg-[#171719] rounded shadow-2xl flex flex-col"
              style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
            >
              {/* Back header */}
              <div className="flex items-center px-5 h-14 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
                <button
                  onClick={() => setFlipped(false)}
                  className="p-1.5 -ml-1 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                  <Icon name="arrow_back" size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 ml-3">设置</h2>
              </div>
              <div className="flex-1 overflow-y-auto">
                {settingsPanel}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
